import api, { apiLongTimeout, twUrl } from '../../utils/api';
import { BaseQueryParams } from '../Global.model';
import {
  InvoiceSummaryResponse,
  InvoiceCreateData,
  ISearchInvoiceParams,
  InvoiceUpdateData,
  InvoiceUpdateDataV3,
  INVOICE_STATUS,
  InvoiceResponseV3,
  QBOCustomField,
  ValidateDivisionsResponse,
  InvoiceIdsByDivision,
  InvoicePreviewPdfData
} from './Invoices.model';

class InvoicesService {
  /**
   * Gets invoice by id
   * @param invoiceId
   */
  static getInvoice(invoiceId: string): Promise<{ data: InvoiceResponseV3 }> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.get(twUrl.v3.getInvoice(invoiceId));

      apiResponse
        .then((response: any) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   * Gets invoice pdf by id
   * @param invoiceId
   */
  static getInvoicePdf(invoiceId: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.get(twUrl.v3.getInvoicePdf(invoiceId), {
        responseType: 'arraybuffer',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/pdf'
        }
      });

      apiResponse
        .then((response) => {
          resolve(new Blob([response.data], { type: 'application/pdf' }));
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   * Updates invoice data
   * @param id invoice id
   * @param invoiceData invoice information to update
   */
  static updateInvoice(
    id: string,
    invoiceData: Partial<InvoiceUpdateDataV3>
  ): Promise<{ status: number; message: string }> {
    return new Promise((resolve, reject) => {
      const apiResponse = apiLongTimeout.put(twUrl.v3.updateInvoice(id), invoiceData);

      apiResponse
        .then((response) => {
          const responseData = {
            status: response.status,
            message: response.data.message
          };
          resolve(responseData);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   * mark as paid
   * @param id invoice id
   */
  /*  static markAsPaid(id: string): Promise<{ status: number; message: string }> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.post(twUrl.v3.markAsPaid(id));

      apiResponse
        .then((response) => {
          const responseData = {
            status: response.status,
            message: response.data.message
          };
          resolve(responseData);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  } */

  /**
   * Deletes invoice
   * @param invoiceId
   */
  static deleteInvoice(invoiceId: string): Promise<number> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.delete(twUrl.v3.deleteInvoice(invoiceId));

      apiResponse
        .then((response) => {
          resolve(response.status);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   * Generate (Truckbase) invoice preview with v2 invoices api
   * @param invoice Invoice data
   */
  static generateInvoicePreviewV2(
    invoiceData: InvoiceCreateData | InvoiceUpdateData
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const request = api.post(twUrl.v2.invoicePreview, invoiceData, {
        responseType: 'arraybuffer',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/pdf'
        }
      });
      request
        .then((response) => {
          resolve(new Blob([response.data], { type: 'application/pdf' }));
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   * Generate (Truckbase) invoice preview with v3 invoices api
   * @param invoice Invoice data
   */
  static generateInvoicePreviewV3(
    invoiceData: InvoicePreviewPdfData
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const request = api.post(twUrl.v3.invoicePreview, invoiceData, {
        responseType: 'arraybuffer',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/pdf'
        }
      });
      request
        .then((response) => {
          resolve(new Blob([response.data], { type: 'application/pdf' }));
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   * Returns a CSV with all invoices
   * @returns CSV file
   */
  static exportInvoicesAsCSV(body: ISearchInvoiceParams): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const apiResponse = api.post(twUrl.v2.exportInvoicesAsCSV(), body, {
        responseType: 'text',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/csv'
        }
      });

      apiResponse
        .then((response) => {
          resolve(new Blob([response.data]));
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   *
   * @param filterParams accepts from and to dates as strings
   * @param queryParams accepts BaseQueryParams for pagination
   */
  static searchInvoices(
    filterParams?: ISearchInvoiceParams,
    queryParams?: BaseQueryParams
  ): Promise<{ content: InvoiceSummaryResponse[]; totalItems: number }> {
    return new Promise((resolve, reject) => {
      const request = api.post(twUrl.v3.invoices, filterParams, { params: queryParams });
      request
        .then((response: any) => {
          const data = {
            content: response.data.data,
            totalItems: response.data.total
          };
          resolve(data);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   *
   * @param filterParams accepts from and to dates as strings
   * @param queryParams accepts BaseQueryParams for pagination
   */
  static fetchFullObjects(
    filterParams?: ISearchInvoiceParams,
    queryParams?: BaseQueryParams
  ): Promise<{ content: InvoiceResponseV3[]; totalItems: number }> {
    return new Promise((resolve, reject) => {
      const request = api.post(twUrl.v3.invoicesAll, filterParams, { params: queryParams });
      request
        .then((response: any) => {
          const data = {
            content: response.data.data,
            totalItems: response.data.total
          };
          resolve(data);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  /**
   *
   * @param filterParams accepts from and to dates as strings
   * @param queryParams accepts BaseQueryParams for pagination
   */
  static fetchInvoiceIdsByDivision(
    filterParams?: ISearchInvoiceParams,
    queryParams?: BaseQueryParams
  ): Promise<{ content: InvoiceIdsByDivision[]; totalItems: number }> {
    return new Promise((resolve, reject) => {
      const request = api.post(twUrl.v3.idDivisionInvoices, filterParams, { params: queryParams });
      request
        .then((response: any) => {
          const data = {
            content: response.data.data,
            totalItems: response.data.total
          };
          resolve(data);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }

  static bulkUpdateInvoicesStatus = (
    status: INVOICE_STATUS,
    filterParams?: ISearchInvoiceParams
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const request = api.post(twUrl.v2.bulkUpdateInvoiceStatus, { status, ...filterParams });
      request
        .then((response: any) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error.response.data);
        });
    });
  };

  static getInvoiceHistory = (invoiceId: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      const request = api.get(twUrl.v2.getInvoiceHistory(invoiceId));
      request
        .then((response) => {
          resolve(response.data.data);
        })
        .catch((error) => reject(error.response.data));
    });
  };

  static getQBOCustomFields = (divisionId: string): Promise<QBOCustomField[]> => {
    return new Promise((resolve, reject) => {
      const request = api.get(twUrl.v3.getQBOCustomFields, { params: { divisionId } });
      request
        .then((response) => {
          resolve(response.data.customFields);
        })
        .catch((error) => reject(error.response.data));
    });
  };

  /**
   * POST validate if the invoices are in the same division
   */
  static validateDivisions(invoiceIds: string[]): Promise<ValidateDivisionsResponse> {
    return new Promise((resolve, reject) => {
      const request = api.post(twUrl.v3.sameDivisions, { invoiceIds });

      request
        .then((response) => resolve(response.data))
        .catch((error) => {
          reject(error.response.data);
        });
    });
  }
}

export default InvoicesService;
